import { useCallback, useEffect, useState } from "react";
import { LogOut, Plus, Send, Settings2, Car } from "lucide-react";
import { toast } from "sonner";
import { LogoMark } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import VehicleCard from "@/components/dashboard/VehicleCard";
import VehicleForm from "@/components/dashboard/VehicleForm";
import RenewDialog from "@/components/dashboard/RenewDialog";
import SettingsDialog from "@/components/dashboard/SettingsDialog";
import SignIn from "@/components/dashboard/SignIn";

export default function Dashboard() {
  const { authed, user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const [renewing, setRenewing] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/overview");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  if (authed === null) {
    return (
      <div className="min-h-screen bg-ink flex items-center justify-center">
        <p className="font-mono text-xs tracking-[0.3em] text-low uppercase animate-pulse" data-testid="auth-checking">
          Checking session
        </p>
      </div>
    );
  }
  if (!authed) return <SignIn />;

  const vehicles = data?.vehicles ?? [];
  const settings = data?.settings ?? { enabled: true, reminder_email: "", thresholds: [30, 14, 7, 1] };
  const events = data?.events ?? [];

  const allCounts = vehicles.flatMap((v) => Object.values(v.counts || {})).filter(Boolean);
  const critical = allCounts.filter((c) => c.status === "overdue" || c.status === "critical").length;
  const soon = allCounts.filter((c) => c.status === "warning").length;
  const remindersOn = settings.enabled && !!settings.reminder_email;

  const stats = [
    { label: "Vehicles", value: vehicles.length, cls: "text-hi", testid: "telemetry-vehicles" },
    { label: "Critical ≤ 7 days", value: critical, cls: critical ? "text-ember" : "text-hi", testid: "telemetry-critical" },
    { label: "Due ≤ 30 days", value: soon, cls: soon ? "text-amber2" : "text-hi", testid: "telemetry-soon" },
    { label: "Reminders", value: remindersOn ? "ON" : "OFF", cls: remindersOn ? "text-mint" : "text-amber2", testid: "telemetry-reminders" },
  ];

  const sendTest = async () => {
    try {
      await api.post("/settings/send-test");
      toast.success(`Test email sent to ${settings.reminder_email}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (v) => {
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success(`${v.plate} removed from the fleet`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-ink text-hi" data-testid="dashboard-page">
      <header className="sticky top-0 z-40 border-b border-line bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoMark size={26} />
            <span className="font-display text-lg font-black uppercase tracking-wider">Fleet Command</span>
            {user?.email && (
              <span className="hidden md:inline font-mono text-[10px] uppercase tracking-[0.15em] text-low border border-line rounded-full px-3 py-1">
                {user.email}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="send-test-email-button"
              onClick={sendTest}
              className="hidden sm:inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-mid hover:text-hi hover:border-linehi transition"
            >
              <Send className="w-3.5 h-3.5" /> Test email
            </button>
            <button
              data-testid="settings-button"
              onClick={() => setSettingsOpen(true)}
              className="inline-flex items-center gap-2 rounded-full border border-line px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-mid hover:text-hi hover:border-linehi transition"
            >
              <Settings2 className="w-3.5 h-3.5" /> Reminders
            </button>
            <button
              data-testid="logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full bg-ember px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] font-bold text-ink hover:bg-ember/90 transition"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 md:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((s) => (
            <button
              key={s.testid}
              data-testid={s.testid}
              onClick={() => s.testid === "telemetry-reminders" && setSettingsOpen(true)}
              className="text-left rounded-2xl border border-line bg-panel p-5 hover:border-linehi transition-colors"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-low">{s.label}</p>
              <p className={`font-display text-4xl font-black mt-2 ${s.cls}`}>{s.value}</p>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 mb-6">
          <h2 className="font-display text-3xl md:text-4xl font-black uppercase tracking-wide">The garage wall</h2>
          <button
            data-testid="add-vehicle-button"
            onClick={() => setEditing("new")}
            className="inline-flex items-center gap-2 rounded-full bg-ember px-5 py-2.5 font-display text-base font-bold uppercase tracking-wider text-ink hover:bg-ember/90 transition"
          >
            <Plus className="w-4 h-4" /> Add vehicle
          </button>
        </div>

        {vehicles.length === 0 ? (
          <div data-testid="empty-fleet" className="rounded-2xl border border-dashed border-line bg-panel/50 p-14 text-center">
            <Car className="w-8 h-8 text-low mx-auto" />
            <p className="font-display text-2xl font-bold uppercase mt-4">Your garage is empty</p>
            <p className="text-mid text-sm mt-2 max-w-sm mx-auto">
              Add your first vehicle to start watching MOT, council test and road tax deadlines.
            </p>
            <button
              onClick={() => setEditing("new")}
              className="mt-6 rounded-full bg-ember px-6 py-2.5 font-display font-bold uppercase tracking-wider text-ink hover:bg-ember/90 transition"
            >
              Add vehicle
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {vehicles.map((v, i) => (
              <VehicleCard
                key={v.id}
                vehicle={v}
                index={i}
                onEdit={setEditing}
                onDelete={() => del(v)}
                onRenew={(vehicle, fieldId) => setRenewing({ vehicle, fieldId })}
              />
            ))}
          </div>
        )}

        {events.length > 0 && (
          <section className="mt-14">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.25em] text-low">Recent renewals</h3>
            <div className="mt-4 space-y-2">
              {events.map((ev) => (
                <div
                  key={ev.id}
                  data-testid="renewal-log-row"
                  className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-xl border border-line bg-panel px-4 py-3 font-mono text-xs text-mid"
                >
                  <span className="uk-plate rounded px-1.5 py-0.5 text-[11px] font-bold">{ev.plate}</span>
                  <span className="text-hi uppercase">{ev.kind}</span>
                  <span>{ev.from || "—"} → {ev.to}</span>
                  <span className="text-low ml-auto">{(ev.at || "").slice(0, 10)}</span>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      <VehicleForm
        open={!!editing}
        vehicle={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={load}
      />
      <RenewDialog
        open={!!renewing}
        target={renewing}
        onClose={() => setRenewing(null)}
        onSaved={load}
      />
      <SettingsDialog
        open={settingsOpen}
        settings={settings}
        onClose={() => setSettingsOpen(false)}
        onSaved={load}
      />
    </div>
  );
}
